import "./ui/optics.css";
import {
  loadDescriptor,
  ParentBridge,
  parseAllowedParentOrigins,
  resolveBridgeTarget,
  resolveMarkerLibrary,
  TrackingManager,
  type ExperienceDescriptor,
  type TrackingBackend,
} from "@visorxr/core";
import { ARScene, loadSignage } from "@visorxr/renderer";
import { GyroBackend } from "./backends/gyro-backend";
import { WebXRBackend } from "./backends/webxr-backend";
import type { EngineContext } from "./engine/engine-context";
import { UiShell } from "./ui/shell";
import { requestMotionPermission } from "./platform/motion-permission";
import { classifyStartError, evaluateStartupGate } from "./engine/start-error";
import { isQuickLookSupported, presentQuickLook } from "./quicklook/quicklook";
import { createTelemetry } from "./telemetry/telemetry";
import { describeDevice } from "./telemetry/device-info";

type Backend = TrackingBackend<EngineContext>;

// Where captured photos may be delivered. The allowlist comes from TRUSTED build config, never the
// descriptor (which is fetchable via ?descriptor= and so attacker-influenceable). resolveBridgeTarget
// fails closed: an unset/empty allowlist authorizes nobody. The fallback to our own origin keeps
// same-origin (reverse-proxy) embeds working by default while a cross-origin parent that isn't on
// the allowlist receives nothing. Set VITE_ALLOWED_PARENT_ORIGINS for cross-origin production embeds.
const allowedParentOrigins = parseAllowedParentOrigins(import.meta.env.VITE_ALLOWED_PARENT_ORIGINS);
const bridgeTarget = resolveBridgeTarget(document.referrer, allowedParentOrigins) ?? window.location.origin;
// Unset means telemetry is off, which is also how it is turned off; only a same-origin path is
// accepted, so a beacon never becomes a cross-origin request. See docs/17-telemetry.md.
const telemetry = createTelemetry(import.meta.env.VITE_TELEMETRY_URL, {
  build: import.meta.env.VITE_BUILD ?? "dev",
  device: () => describeDevice(navigator, window.screen, window.devicePixelRatio),
});
const bridge = new ParentBridge(
  (message, origin) => {
    // Tapped here rather than at each reporting site: the bridge vocabulary already *is* the
    // session-outcome vocabulary, so a future error site cannot forget to report itself.
    telemetry?.recordBridge(message);
    window.parent?.postMessage(message, origin);
  },
  bridgeTarget,
);

/**
 * Campaign and experience identifiers reach us from the query string and from a descriptor that can
 * be fetched from anywhere, and they ride in every telemetry chunk. Anything unrecognisable is
 * reported as such rather than carried.
 */
function safeLabel(value: string): string {
  return /^[\w-]{1,64}$/.test(value) ? value : "other";
}

void bootstrap();

async function bootstrap(): Promise<void> {
  try {
    const params = new URLSearchParams(location.search);
    const exp = params.get("exp") ?? "demo";
    const descriptorUrl = params.get("descriptor") ?? `/descriptors/${exp}.json`;

    // The descriptor URL itself is attacker-influenceable, so only the fact that one was used is
    // recorded — never the value.
    telemetry?.setEnvelope({ exp: params.has("descriptor") ? "custom" : safeLabel(exp) });

    const result = await loadDescriptor(descriptorUrl, (url) => fetch(url));
    if (!result.ok) throw new Error(result.error);
    telemetry?.setEnvelope({ campaignId: safeLabel(result.value.id) });

    await run(result.value);
  } catch (error) {
    bridge.error("INIT_FAILED", asMessage(error));
    document.body.append(errorBanner(asMessage(error)));
  }
}

async function run(descriptor: ExperienceDescriptor): Promise<void> {
  // iOS/iPadOS: hand off to native AR Quick Look (ARKit) — no canvas, no getUserMedia, no SLAM.
  // Branding is baked into the USDZ; Apple owns capture/share, so there's no photo-captured bridge.
  // Marker descriptors are excluded first: the poster path is canvas-rendered on iOS too (that is
  // the point of image tracking), so it must never fall into a branch built around `model.usdzUrl`.
  // Either authoring shape counts as image-target content — a library is N markers, not none.
  const markerLibrary = resolveMarkerLibrary(descriptor);
  const hasMarkerContent = markerLibrary !== null;
  telemetry?.setEnvelope({ markers: markerLibrary?.entries.length ?? 0 });
  // Read from the platform, not from any element: an inert frame-delivery watch must be
  // distinguishable from a healthy one when a pull shows zero video-stalled events.
  telemetry?.setEnvelope({ videoQualityApi: "getVideoPlaybackQuality" in HTMLVideoElement.prototype });
  if (!hasMarkerContent && isQuickLookSupported() && descriptor.model?.usdzUrl) {
    runQuickLook(descriptor, descriptor.model.usdzUrl);
    return;
  }

  const scene = new ARScene();
  scene.renderer.domElement.className = "visor-canvas";
  document.body.append(scene.renderer.domElement);
  const resize = () => scene.setSize(window.innerWidth, window.innerHeight);
  resize();
  window.addEventListener("resize", resize);

  // A marker-only descriptor ships no glTF. A hybrid one (marker + model, falling back from image
  // tracking to a placed glTF) can't be resolved yet either — which backend wins isn't known until
  // onStart() — so loading here would place the model at the scene origin under a live image-
  // tracking session with nothing to move it. Deferred to onStart() for that case; loaded here
  // (unchanged timing, so Start has zero added latency) for every descriptor without a marker,
  // which is every descriptor in the field today.
  if (descriptor.model && !hasMarkerContent) {
    scene.setSignage(await loadSignage(descriptor.model));
  }

  let active: Backend | null = null;
  const shell = new UiShell(document.body, descriptor, { onStart, onCapture });
  const ctx: EngineContext = { scene, descriptor, overlayRoot: shell.root };

  shell.showLoading("Checking compatibility…");
  // Probe support on load — NOT inside the Start gesture — so the tap stays a valid user activation.
  const manager = new TrackingManager<EngineContext>({ prefer: descriptor.tracking.prefer });
  const candidates: Backend[] = [new WebXRBackend(), new GyroBackend()];
  // Dynamic-imported so a glTF-only experience never pays for the tracker/detector chunk — the
  // descriptor validator (core/src/descriptor.ts) already refuses "image" in tracking.prefer
  // without a marker, so gating the import on marker presence covers every descriptor that could
  // actually reach TrackingManager wanting it.
  if (hasMarkerContent) {
    const { ImageBackend } = await import("./backends/image-backend");
    const { worstVideoTexturePath } = await import("@visorxr/renderer");
    let worstTexturePath: ReturnType<typeof worstVideoTexturePath> | null = null;
    candidates.push(
      new ImageBackend({
        observe: telemetry?.observe,
        // WebKit refuses audible playback outside a gesture, and the Start tap is long spent by the
        // time an overlay plays (docs/12 §4.6). The tap this button captures is the fresh gesture
        // that lifts the refusal.
        onAudioBlocked: (unmute) => {
          if (!unmute) {
            shell.hideUnmute();
            return;
          }
          telemetry?.audioBlocked();
          shell.showUnmute(() => {
            unmute();
            telemetry?.unmuted();
          });
        },
        // The audio-only-pipeline finding (docs/12 §4.6): the heal happens in the backend; the
        // report is what lets the field say how often it fires and whether it works.
        onVideoStall: (report, target) => {
          if (report === "video-stalled") telemetry?.videoStalled(target);
          else telemetry?.videoRecovered(target);
        },
        // Which route delivers overlay pixels — `relay`/`dead` is the WKWebView upload failure
        // (Chrome on iOS). Worst-wins across surfaces: with several posters deciding, the session
        // reading must not depend on which the user happened to scan last.
        onVideoTexturePath: (path) => {
          worstTexturePath = worstVideoTexturePath(worstTexturePath, path);
          telemetry?.setEnvelope({ videoTexturePath: worstTexturePath });
        },
      }),
    );
  }
  const ordered = await manager.orderedSupported(candidates);
  telemetry?.setEnvelope({ supported: ordered.map((backend) => backend.id) });

  // Gate startup: an insecure (HTTP) origin hides the sensor APIs, so report that — not a generic
  // "unsupported device" — and only otherwise fall through to "no supported backend".
  const gate = evaluateStartupGate({
    isSecureContext: window.isSecureContext,
    supportedCount: ordered.length,
  });
  if (gate) {
    shell.showError(gate.message);
    bridge.error(gate.code, gate.message);
    return;
  }

  shell.showGate();
  bridge.ready();

  async function onStart(): Promise<void> {
    shell.showLoading("Starting AR…");
    // Order matters and is empirically validated (Q1 spike, real iOS 18 Safari): requestPermission
    // needs the transient activation from this tap and runs first; getUserMedia / requestSession
    // (which don't strictly require activation) follow inside startFirst. Must be inside the iframe.
    try {
      await requestMotionPermission();
      telemetry?.setEnvelope({ motion: "ok" });
    } catch {
      // The gyro backend will still attempt to start; a hard failure surfaces below.
      telemetry?.setEnvelope({ motion: "denied" });
    }

    const result = await startFirst(ordered, ctx);
    if ("error" in result) {
      const failure = classifyStartError(result.error);
      shell.showError(failure.message);
      bridge.error(failure.code, failure.message);
      return;
    }

    // The hybrid case deferred from run(): only load the glTF once the winning backend is known to
    // be one that places it. If image tracking won, there is nothing to place — and setSignage-ing
    // it anyway would render it at the scene origin, which is also where the camera starts.
    //
    // The backend stays local until every fallible step has passed: publishing it first and then
    // throwing leaves the session started, the camera held, and the shell stuck on "Starting AR…"
    // with the rejection swallowed by the click handler.
    const backend = result.backend;
    telemetry?.started(backend.id);
    if (descriptor.model && hasMarkerContent && backend.id !== "image") {
      try {
        scene.setSignage(await loadSignage(descriptor.model));
      } catch (error) {
        backend.stop();
        const message = `Couldn't load the AR model: ${asMessage(error)}`;
        shell.showError(message);
        bridge.error("MODEL_LOAD_FAILED", message);
        return;
      }
    }

    active = backend;
    shell.showTracking();
    startRenderLoop(scene, backend);
  }

  async function onCapture(): Promise<void> {
    if (!active) return;
    shell.setCapturing(true);
    try {
      const blob = await active.capture({
        watermarkUrl: descriptor.branding?.watermark,
        mimeType: "image/jpeg",
        quality: 0.92,
      });
      const dataURL = await blobToDataURL(blob);
      bridge.photo(dataURL);
      // Show the shot in-player (preview + share/save). Also posted to the parent via bridge above.
      showCaptureResult(blob, dataURL, descriptor, shell.root);
    } catch (error) {
      bridge.error("CAPTURE_FAILED", asMessage(error));
      showCaptureError(asMessage(error), shell.root);
    } finally {
      shell.setCapturing(false);
    }
  }
}

type StartResult = { backend: Backend } | { error: unknown };

/** Start the first backend that succeeds; keep the last error so the user gets a specific reason. */
async function startFirst(ordered: Backend[], ctx: EngineContext): Promise<StartResult> {
  let lastError: unknown = new Error("no supported tracking backend");
  for (const backend of ordered) {
    try {
      await backend.start(ctx);
      return { backend };
    } catch (error) {
      // Try the next supported backend (e.g. WebXR without camera-access → gyro). stop() must be
      // safe to call on a backend whose start() failed at any point — every backend upholds that
      // (each is null-guarded/idempotent) — so this is what releases whatever the failed attempt
      // partially acquired (camera, session, worker) before falling through to the next candidate.
      backend.stop();
      lastError = error;
    }
  }
  return { error: lastError };
}

function startRenderLoop(scene: ARScene, backend: Backend): void {
  let last = performance.now();
  const onFrame = (_time: number, frame?: XRFrame): void => {
    const now = performance.now();
    const dt = Math.max((now - last) / 1000, 1e-3);
    last = now;

    // The rate the tracker is *asked* at, which is the loop's own health rather than the camera's.
    // Recorded here, above any backend, because a starved loop starves whichever one is running.
    telemetry?.observe({ kind: "sample", tMs: now });

    const pose = backend.update(dt, frame);
    if (!backend.caps.sixDoF) scene.setCameraPose(pose.camera);
    scene.setObjectPose(pose.object);
    scene.render();
  };

  // While an immersive session presents, three stops its plain rAF loop (on 'sessionstart') and the
  // XR session drives frames — each carrying the XRFrame that Raw Camera Access capture needs.
  // renderer.setAnimationLoop would RESTART that plain loop (animation.start()), adding a second,
  // frame-less loop that races the capture drain. Register on the XR manager so only the XR loop runs.
  const { renderer } = scene;
  if (renderer.xr.isPresenting) {
    renderer.xr.setAnimationLoop(onFrame);
  } else {
    renderer.setAnimationLoop(onFrame);
  }
}

/**
 * Full-screen capture preview inside the player: the photo + Share/Save + Retake. Appended to the
 * shell root so it composites over the WebXR dom-overlay too. The photo is also posted to the parent
 * via the bridge; this gives visible feedback (and works standalone / when the parent ignores it).
 */
function showCaptureResult(
  blob: Blob,
  dataURL: string,
  descriptor: ExperienceDescriptor,
  root: HTMLElement,
): void {
  const overlay = document.createElement("div");
  overlay.className = "visor-shell";
  overlay.style.cssText = "display:flex;flex-direction:column;background:rgba(0,0,0,.92);z-index:30";

  const img = document.createElement("img");
  img.src = dataURL;
  img.alt = "capture";
  img.style.cssText = "flex:1;min-height:0;width:100%;object-fit:contain";

  const bar = document.createElement("div");
  bar.style.cssText =
    "display:flex;gap:12px;justify-content:center;padding:16px 16px calc(16px + env(safe-area-inset-bottom))";

  const share = document.createElement("button");
  share.className = "visor-btn";
  share.textContent = "Share / Save";
  share.addEventListener("click", () => void shareOrDownload(blob, descriptor));

  const retake = document.createElement("button");
  retake.className = "visor-btn";
  retake.style.cssText = "background:#1d232b;color:#e7eaee";
  retake.textContent = "Retake";
  retake.addEventListener("click", () => overlay.remove());

  bar.append(share, retake);
  overlay.append(img, bar);
  root.append(overlay);
}

/** Native share when the platform supports sharing files, otherwise download the blob. */
async function shareOrDownload(blob: Blob, descriptor: ExperienceDescriptor): Promise<void> {
  const file = new File([blob], `${descriptor.id}.jpg`, { type: blob.type });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: descriptor.share?.title ?? descriptor.title });
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return; // user cancelled
    }
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${descriptor.id}.jpg`;
  link.click();
  URL.revokeObjectURL(url);
}

/** Dismissible capture-failure panel — previously errors only went to the bridge (invisible standalone). */
function showCaptureError(message: string, root: HTMLElement): void {
  const overlay = document.createElement("div");
  overlay.className = "visor-shell";
  const panel = document.createElement("div");
  panel.className = "visor-glass visor-panel visor-error";
  const title = document.createElement("h1");
  title.className = "visor-title";
  title.textContent = "Capture failed";
  const sub = document.createElement("p");
  sub.className = "visor-sub";
  sub.textContent = message;
  const close = document.createElement("button");
  close.className = "visor-btn";
  close.textContent = "Close";
  close.addEventListener("click", () => overlay.remove());
  panel.append(title, sub, close);
  overlay.append(panel);
  root.append(overlay);
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function runQuickLook(descriptor: ExperienceDescriptor, usdzUrl: string): void {
  let launched = false;
  document.body.append(
    quickLookLauncher(descriptor, () => {
      if (!presentQuickLook(usdzUrl)) {
        document.body.append(errorBanner("AR Quick Look is unavailable on this device."));
        return;
      }
      // The handoff to Apple's viewer is where this session begins — rendering the button is not.
      // Reported once: the launcher stays on the page and can be tapped again.
      if (launched) return;
      launched = true;
      telemetry?.started("quicklook");
    }),
  );
  bridge.ready();
}

/** Minimal Optics launch panel for the AQL path: a title + a single "View in AR" gesture. */
function quickLookLauncher(descriptor: ExperienceDescriptor, onView: () => void): HTMLElement {
  const root = document.createElement("div");
  root.className = "visor-shell";
  const panel = document.createElement("div");
  panel.className = "visor-glass visor-panel";

  const title = document.createElement("h1");
  title.className = "visor-title";
  title.textContent = descriptor.title;

  const sub = document.createElement("p");
  sub.className = "visor-sub";
  sub.textContent = "See it in your space — tap to view in AR.";

  const button = document.createElement("button");
  button.className = "visor-btn";
  button.type = "button";
  button.textContent = "View in AR";
  button.addEventListener("click", onView);

  panel.append(title, sub, button);
  root.append(panel);
  return root;
}

function errorBanner(message: string): HTMLElement {
  const node = document.createElement("div");
  node.className = "visor-shell";
  node.innerHTML = `<div class="visor-glass visor-panel visor-error"><h1 class="visor-title">Something went wrong</h1><p class="visor-sub"></p></div>`;
  node.querySelector(".visor-sub")!.textContent = message;
  return node;
}

function asMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
